import { io } from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 2; // Reduced from 5 to 2
    this.reconnectDelay = 500; // Reduced from 1000ms to 500ms
    this.maxReconnectDelay = 2000; // Reduced from 30s to 2s
    this.listeners = new Map();
    this.sessionId = null;
    this.connectionPromise = null;
  }

  connect(serverUrl = process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:5002') {
    if (this.socket && this.isConnected) {
      return Promise.resolve();
    }

    // Return existing connection promise if already connecting
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        // Initialize socket connection with reduced timeouts
        this.socket = io(serverUrl, {
          transports: ['websocket', 'polling'],
          timeout: 2000, // Reduced from 10s to 2s
          reconnection: true,
          reconnectionAttempts: this.maxReconnectAttempts,
          reconnectionDelay: this.reconnectDelay,
          reconnectionDelayMax: this.maxReconnectDelay,
          maxHttpBufferSize: 1e6, // 1MB
          pingTimeout: 5000, // Reduced from 60s to 5s
          pingInterval: 2000 // Reduced from 25s to 2s
        });

        // Connection event handlers
        this.socket.on('connect', () => {
          console.log('Socket connected:', this.socket.id);
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000; // Reset delay
          this.connectionPromise = null;
          resolve();
        });

        this.socket.on('disconnect', (reason) => {
          console.log('Socket disconnected:', reason);
          this.isConnected = false;
          this.connectionPromise = null;

          // Emit disconnect event to listeners
          this.emit('disconnect', { reason });
        });

        this.socket.on('connect_error', (error) => {
          console.error('Socket connection error:', error);
          this.isConnected = false;
          this.connectionPromise = null;

          // Emit connection error to listeners
          this.emit('connect_error', { error: error.message });

          if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            reject(new Error(`Failed to connect after ${this.maxReconnectAttempts} attempts`));
          }
        });

        this.socket.on('reconnect', (attemptNumber) => {
          console.log(`Socket reconnected after ${attemptNumber} attempts`);
          this.isConnected = true;
          this.reconnectAttempts = 0;

          // Rejoin session if we have one
          if (this.sessionId) {
            this.joinSession(this.sessionId);
          }

          // Emit reconnect event to listeners
          this.emit('reconnect', { attemptNumber });
        });

        this.socket.on('reconnect_attempt', (attemptNumber) => {
          console.log(`Socket reconnection attempt ${attemptNumber}`);
          this.reconnectAttempts = attemptNumber;

          // Exponential backoff for reconnection delay
          this.reconnectDelay = Math.min(
            this.reconnectDelay * 1.5,
            this.maxReconnectDelay
          );

          // Emit reconnect attempt to listeners
          this.emit('reconnect_attempt', { attemptNumber });
        });

        this.socket.on('reconnect_failed', () => {
          console.error('Socket reconnection failed');
          this.isConnected = false;
          this.connectionPromise = null;

          // Emit reconnect failed to listeners
          this.emit('reconnect_failed');
          reject(new Error('Reconnection failed'));
        });

        // Chat-specific event handlers
        this.socket.on('session_joined', (data) => {
          console.log('Session joined:', data.sessionId);
          this.sessionId = data.sessionId;
          this.emit('session_joined', data);
        });

        this.socket.on('message', (message) => {
          console.log('Message received:', message);
          this.emit('message', message);
        });

        this.socket.on('typing', (data) => {
          this.emit('typing', data);
        });

        this.socket.on('error', (error) => {
          console.error('Socket error:', error);
          this.emit('error', error);
        });

        // Pong handler for connection health checks
        this.socket.on('pong', () => {
          this.emit('pong');
        });

      } catch (error) {
        console.error('Error initializing socket:', error);
        this.connectionPromise = null;
        reject(error);
      }
    });

    return this.connectionPromise;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.sessionId = null;
      this.connectionPromise = null;
      this.listeners.clear();
    }
  }

  // Session management
  joinSession(sessionId = null) {
    if (!this.isConnected || !this.socket) {
      throw new Error('Socket not connected');
    }

    console.log('Joining session:', sessionId);
    this.socket.emit('join_session', { sessionId });
  }

  // Message handling
  sendMessage(message, type = 'text', options = {}) {
    if (!this.isConnected || !this.socket) {
      throw new Error('Socket not connected');
    }

    if (!message || typeof message !== 'string' || message.trim().length === 0) {
      throw new Error('Invalid message');
    }

    console.log('Sending message:', message);

    const payload = {
      message: message.trim(),
      type
    };

    // Add language to userPreferences in metadata if provided
    if (options.language) {
      payload.metadata = {
        userPreferences: {
          language: options.language
        }
      };
    }

    this.socket.emit('send_message', payload);
  }

  // Typing indicators
  setTyping(isTyping) {
    if (!this.isConnected || !this.socket) {
      return;
    }

    this.socket.emit('typing', { isTyping });
  }

  // Connection health check
  ping() {
    if (!this.isConnected || !this.socket) {
      return false;
    }

    this.socket.emit('ping');
    return true;
  }

  // Event listener management
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }
  }

  // Utility methods
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      socketId: this.socket?.id,
      sessionId: this.sessionId,
      reconnectAttempts: this.reconnectAttempts
    };
  }

  isHealthy() {
    return this.isConnected && this.socket && this.socket.connected;
  }
}

// Create singleton instance
const socketService = new SocketService();

export default socketService;